// Brand logo for "my NDIS Care and Support HUB".
// Short form on the wordmark: "my NDIS CaS HUB".
// Full name shown as a tagline below when `showFullName` is true.
//
// Visual reference: jigsaw-puzzle motif from /artifacts/NDIS puzzle.jpg —
// deep wine background, mint puzzle pieces, white "ndis" letters, lime dot on the "i".

export default function Logo({
  size = 36,
  className = "",
  showFullName = false,
  variant = "auto",          // "auto" | "light" | "dark"
}) {
  // Auto contrast: wordmark color picks up theme; badge is always wine + mint.
  const wordmarkClass =
    variant === "light" ? "text-white" :
    variant === "dark" ? "text-foreground" :
    "text-foreground";
  // "CaS" accent color: lime on dark/wine bg (variant=light), wine on light bg.
  const casColor = variant === "light" ? "hsl(var(--brand-lime))" : "hsl(var(--brand-wine))";

  return (
    <div className={`flex items-center gap-3 ${className}`} data-testid="app-logo">
      <PuzzleBadge size={size} />
      <div className="leading-tight min-w-0">
        <div className={`font-display font-black tracking-tight ${wordmarkClass}`}
             style={{ fontSize: Math.max(14, Math.round(size * 0.46)) }}
             data-testid="app-logo-wordmark">
          <span className="font-light italic mr-0.5 opacity-70">my</span>
          <span>NDIS</span>{" "}
          <span style={{ color: casColor }}>CaS</span>{" "}
          <span>HUB</span>
        </div>
        {showFullName && (
          <div className={`text-[10px] uppercase tracking-[0.18em] font-bold mt-0.5 ${variant === "light" ? "text-white/70" : "text-muted-foreground"}`}
               data-testid="app-logo-fullname">
            my NDIS Care and Support HUB
          </div>
        )}
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Puzzle badge — 4 interlocking pieces, mint on wine, with a lime dot.
// Pure SVG so it scales crisply at any size.
// ----------------------------------------------------------------------------
function PuzzleBadge({ size = 36 }) {
  // Two puzzle pieces with characteristic tab + slot, arranged in opposing colors.
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      role="img"
      aria-label="my NDIS Care and Support Hub"
      data-testid="app-logo-badge"
      style={{ flexShrink: 0 }}
    >
      <defs>
        <clipPath id="logo-rounded">
          <rect x="0" y="0" width="64" height="64" rx="14" ry="14" />
        </clipPath>
      </defs>
      <g clipPath="url(#logo-rounded)">
        {/* Wine background */}
        <rect x="0" y="0" width="64" height="64" fill="hsl(var(--brand-wine))" />

        {/* Mint puzzle piece — top-left tab pointing right, bottom slot */}
        <path
          d="M4,4 H30 a4,4 0 0 1 4,4 v4 a4,4 0 0 0 4,4 h0 a4,4 0 0 1 0,8 h0 a4,4 0 0 0 -4,4 v4 a4,4 0 0 1 -4,4 H4 Z"
          fill="hsl(var(--brand-mint))"
        />
        {/* Mint puzzle piece — bottom-right, mirrored, with tab pointing up */}
        <path
          d="M60,60 H34 a4,4 0 0 1 -4,-4 v-4 a4,4 0 0 0 -4,-4 h0 a4,4 0 0 1 0,-8 h0 a4,4 0 0 0 4,-4 v-4 a4,4 0 0 1 4,-4 H60 Z"
          fill="hsl(var(--brand-mint))"
          opacity="0.85"
        />

        {/* "ndis" stacked letters in white — small caps centered */}
        <text
          x="32"
          y="38"
          textAnchor="middle"
          fontFamily="'Bricolage Grotesque', system-ui, sans-serif"
          fontWeight="900"
          fontSize="22"
          fill="#ffffff"
          letterSpacing="-1"
          style={{ fontStretch: "condensed" }}
        >
          ndis
        </text>

        {/* Lime dot on the "i" — slightly above the text baseline */}
        <circle cx="35.5" cy="20" r="3.4" fill="hsl(var(--brand-lime))" />
      </g>
      {/* Subtle inner border */}
      <rect x="0.5" y="0.5" width="63" height="63" rx="13.5" ry="13.5"
            fill="none" stroke="hsl(var(--brand-wine))" strokeOpacity="0.25" />
    </svg>
  );
}
